import { useState } from 'react';
import CONFIG from '../config';
import { subscribeToUploadProgress, deletePptDocument } from '../services/pptService';
import { fetchWithAuth, fetchWithAuthFormData } from '../utils/api';

const useUpload = (useEnhancedThumbnails = false, maxUploadSizeMb = 100) => {
  const CLOUD_FUNCTION_URL = CONFIG.CLOUD_FUNCTION_URL;
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState('');

  // PPT 업로드 핸들러
  // onUploadComplete: small_thumbnail_url이 생성되면 호출 (PPT 카드 생성)
  const handlePptUpload = async (file, userId, onUploadComplete) => {
    if (!file) return;
    
    // 파일 크기 체크 (사용자별 제한 적용)
    const MAX_UPLOAD_SIZE_BYTES = maxUploadSizeMb * 1024 * 1024;
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      const fileSizeMB = Math.round(file.size / (1024 * 1024) * 100) / 100;
      const errorMsg = `파일 크기가 너무 큽니다!\n\n현재 파일 크기: ${fileSizeMB}MB\n최대 허용 크기: ${maxUploadSizeMb}MB\n\n더 작은 파일을 선택해주세요.`;
      setError(errorMsg);
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setUploadDone(false);
    setError('');
    setUploadStatus('파일 업로드 중...');

    const newSessionId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    const CHUNK_SIZE = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    // ✅ 로컬 변수로 리스너 및 docId 관리
    let uploadDocId = null;
    let unsubscribe = null;
    let uploadCompleted = false; // 업로드 완료 콜백 호출 여부

    try {
      // 업로드 초기화
      const initResponse = await fetchWithAuth(`${CLOUD_FUNCTION_URL}/init_upload`, {
        method: 'POST',
        body: JSON.stringify({
          upload_id: newSessionId,
          total_chunks: totalChunks,
          filename: file.name,
          userId: userId
        })
      });
      
      if (!initResponse.ok) {
        if (initResponse.status === 413) {
          const errorData = await initResponse.json();
          if (errorData.error === 'FILE_TOO_LARGE_FOR_ASPOSE') {
            setError(errorData.message);
            setUploading(false);
            setUploadStatus('');
            return;
          }
        }
        throw new Error('Failed to initialize upload');
      }
      
      const { upload_id: uploadId } = await initResponse.json();
      
      // 청크 단위로 파일 업로드
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const formData = new FormData();
        formData.append('file', chunk, file.name);
        formData.append('userId', userId);
        formData.append('session_id', newSessionId);
        formData.append('chunk_index', chunkIndex.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('upload_id', uploadId);
        formData.append('use_enhanced_thumbnails', useEnhancedThumbnails.toString());
        
        const response = await fetchWithAuthFormData(`${CLOUD_FUNCTION_URL}/upload_ppt`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.error) {
          throw new Error(result.error);
        }
        
        setUploadProgress((chunkIndex + 1) / totalChunks * 100);

        // ✅ 첫 번째 청크부터 doc_id를 받으면 즉시 리스너 시작
        if (result.doc_id && !uploadDocId) {
          uploadDocId = result.doc_id;
          console.log(`[useUpload] 업로드 진행률 리스너 시작: ${uploadDocId}`);

          // eslint-disable-next-line no-loop-func
          unsubscribe = subscribeToUploadProgress(uploadDocId, (data) => {
            if (data.error) {
              console.error('❌ 진행률 구독 오류:', data.error);
              setError(data.error);
              setUploadStatus('업로드 실패');
              return;
            }

            // Firestore에서 받은 진행률로 업데이트 (서버 처리 진행률)
            const progressPercent = data.progress !== undefined
              ? Math.round(data.progress)
              : Math.round((data.current / Math.max(data.total, 1)) * 100);

            setUploadProgress(progressPercent);

            // 서버에서 온 상태 메시지 사용
            if (data.status) {
              setUploadStatus(data.status);
            }

            // small_thumbnail_url이 있으면 업로드 완료 콜백 호출 (PPT 카드 생성)
            if (data.small_thumbnail_url && onUploadComplete && !uploadCompleted) {
              console.log(`[useUpload] small_thumbnail_url 수신, 업로드 완료: ${uploadDocId}`);
              uploadCompleted = true;

              // 업로드 완료 콜백 호출 (pdf_url, total_slides 포함)
              onUploadComplete({
                success: true,
                doc_id: uploadDocId,
                slides: data.total_slides,
                small_thumbnail_url: data.small_thumbnail_url,
                pdf_url: data.pdf_url
              });

              // PPT 카드 생성 완료, 스피너 중지
              setUploading(false);

              // 리스너 해제
              if (unsubscribe) {
                console.log('[useUpload] 업로드 완료, 리스너 해제');
                unsubscribe();
                unsubscribe = null;
              }
            }

            if (data.completed) {
              setUploadDone(true);
              setUploadStatus('분석 완료!');
              setUploadProgress(100);
            } else if (data.stage === 'error') {
              console.error('❌ 서버 처리 오류:', data);
              setError(data.error || data.status);
              setUploadStatus('처리 실패');
            }
          });
        }
        
        // 마지막 청크의 응답에 slides가 포함되어 있으면 파일 전송 완료
        // (PPT 카드 생성은 small_thumbnail_url 수신 시점에 처리)
        if (result.slides && result.doc_id) {
          console.log('[useUpload] ✅ 서버 응답 수신:', result);
          console.log(`[useUpload] doc_id: ${result.doc_id}, slides: ${result.slides}`);
          setUploadProgress(100); // 파일 전송 완료
          // PPT 카드 생성은 리스너에서 small_thumbnail_url 수신 시 처리
        }
      }
      
      setUploadDone(true);
      setUploadStatus('분석 완료!');
      
    } catch (e) {
      setUploadStatus('처리 실패');
      setUploading(false); // 에러 발생 시 스피너 중지

      // ✅ 업로드 실패 시 리스너 해제
      if (unsubscribe) {
        console.log('[useUpload] 에러 발생, 리스너 해제');
        unsubscribe();
        unsubscribe = null;
      }

      // ✅ 업로드 실패 시 좀비 문서 삭제
      if (uploadDocId) {
        console.log(`[useUpload] 업로드 실패로 인한 좀비 문서 삭제 시도: ${uploadDocId}`);
        try {
          await deletePptDocument(uploadDocId, userId);
          console.log(`[useUpload] 좀비 문서 삭제 완료: ${uploadDocId}`);
        } catch (deleteError) {
          console.error(`[useUpload] 좀비 문서 삭제 실패: ${uploadDocId}`, deleteError);
        }
      }

      if (e.message.includes('413')) {
        const fileSizeMB = Math.round(file.size / (1024 * 1024) * 100) / 100;
        const errorMsg = `파일이 너무 큽니다.\n현재 파일 크기: ${fileSizeMB}MB\n최대 허용 크기: ${maxUploadSizeMb}MB\n\n더 작은 파일을 선택해주세요.`;
        setError(errorMsg);
      } else {
        try {
          const errorData = JSON.parse(e.message);
          setError(errorData.error || '알 수 없는 오류가 발생했습니다.');
        } catch {
          setError('PPT 업로드 중 오류가 발생했습니다: ' + e.message);
        }
        console.error('Upload error:', e);
      }
    } finally {
      // ✅ uploading 상태는 small_thumbnail_url 수신 시 또는 에러 발생 시 해제됨
      // 리스너도 해당 시점에 해제됨
    }
  };

  const resetUploadStates = () => {
    setUploading(false);
    setUploadProgress(0);
    setUploadDone(false);
    setUploadStatus('');
    setError('');
  };

  return {
    // 상태
    uploading,
    uploadProgress,
    uploadDone,
    uploadStatus,
    error,

    // 함수
    handlePptUpload,
    resetUploadStates,
    setUploadStatus,
    setError
  };
};

export default useUpload;